import React, { useState, useEffect, useCallback } from 'react';
import { Folder, FileText, Download, Upload, ChevronRight, Home, Trash2, FileImage, FileJson } from 'lucide-react';
import { apiRequest } from '../services/api';
import { MarkdownRenderer } from '../components/program/MarkdownRenderer';

interface FileItem {
  path: string;
  name: string;
  size: number;
  sha: string;
  type: 'file' | 'dir';
  downloadUrl?: string;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function extension(path: string): string {
  const parts = path.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

function isTextPreview(name: string): boolean {
  return ['md', 'txt', 'json', 'js', 'ts', 'jsx', 'tsx', 'html', 'css', 'yaml', 'yml', 'xml', 'csv'].includes(extension(name));
}

function isImage(name: string): boolean {
  return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(extension(name));
}

function isPdf(name: string): boolean {
  return extension(name) === 'pdf';
}

function repoNameForUser(email: string): string {
  const safe = email.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase();
  return `client-${safe}`;
}

const FilesView: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDir, setCurrentDir] = useState('');
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [repoReady, setRepoReady] = useState(false);
  const [repoCreating, setRepoCreating] = useState(false);
  const [uploading, setUploading] = useState(false);

  const userEmail = localStorage.getItem('bizbuilder_user_email') || 'unknown';
  const repoName = repoNameForUser(userEmail);

  const loadFiles = useCallback(async (dir: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiRequest(`/api/files/list`, 'POST', { repoName, path: dir });
      setFiles(result.files || []);
      setRepoReady(true);
    } catch (err: any) {
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        setRepoReady(false);
        setFiles([]);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [repoName]);

  useEffect(() => {
    loadFiles(currentDir);
  }, [currentDir, loadFiles]);

  const ensureRepo = async () => {
    setRepoCreating(true);
    try {
      await apiRequest('/api/files/create-repo', 'POST', { repoName, isPrivate: true });
      setRepoReady(true);
      await loadFiles(currentDir);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRepoCreating(false);
    }
  };

  const navigateDir = (path: string) => {
    setSelectedFile(null);
    setFileContent(null);
    setCurrentDir(path);
  };

  const selectFile = async (file: FileItem) => {
    setSelectedFile(file);
    if (isTextPreview(file.name) || isPdf(file.name)) {
      try {
        const result = await apiRequest(`/api/files/download?repo=${repoName}&path=${encodeURIComponent(file.path)}`, 'GET');
        setFileContent(result.content);
      } catch {
        setFileContent(null);
      }
    } else {
      setFileContent(null);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const targetPath = currentDir ? `${currentDir}/${file.name}` : file.name;
      await apiRequest('/api/files/upload', 'POST', { repoName, path: targetPath, content: text, message: `Upload ${file.name}` });
      await loadFiles(currentDir);
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (item: FileItem) => {
    if (!confirm(`Delete ${item.name}?`)) return;
    try {
      await apiRequest('/api/files/delete', 'DELETE', { repoName, path: item.path, message: `Delete ${item.name}` });
      if (selectedFile?.path === item.path) {
        setSelectedFile(null);
        setFileContent(null);
      }
      await loadFiles(currentDir);
    } catch (err: any) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const breadcrumbs = currentDir ? currentDir.split('/') : [];

  return (
    <div className="space-y-4 p-4 pb-24">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Files</h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm cursor-pointer">
            <Upload size={15} />
            {uploading ? 'Uploading...' : 'Upload'}
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading || !repoReady} />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-1 text-sm text-slate-500">
        <button onClick={() => navigateDir('')} className="hover:text-blue-600 transition-colors">
          <Home size={15} />
        </button>
        {breadcrumbs.map((part, i) => {
          const path = breadcrumbs.slice(0, i + 1).join('/');
          return (
            <React.Fragment key={path}>
              <ChevronRight size={12} />
              <button onClick={() => navigateDir(path)} className="hover:text-blue-600 transition-colors">
                {part}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {!repoReady && !loading && (
        <div className="text-center py-12">
          <p className="text-slate-500 mb-4">No repository found for your account</p>
          <button
            onClick={ensureRepo}
            disabled={repoCreating}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {repoCreating ? 'Creating...' : 'Create Repository'}
          </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-12 text-slate-400">Loading files...</div>
      )}

      {repoReady && !loading && files.length === 0 && (
        <div className="text-center py-12">
          <Folder size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">This folder is empty</p>
          <p className="text-slate-400 text-sm mt-1">Upload a file to get started</p>
        </div>
      )}

      {selectedFile && (
        <div className="bg-white border border-slate-100 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              {isImage(selectedFile.name) ? <FileImage size={18} className="text-blue-600" /> :
               isTextPreview(selectedFile.name) ? <FileText size={18} className="text-blue-600" /> :
               <FileJson size={18} className="text-blue-600" />}
              <h3 className="font-bold text-slate-800">{selectedFile.name}</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDelete(selectedFile)}
                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={16} />
              </button>
              <button
                onClick={() => setSelectedFile(null)}
                className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="p-4">
            {isImage(selectedFile.name) && fileContent ? (
              <img
                src={`data:image/${extension(selectedFile.name)};base64,${fileContent}`}
                alt={selectedFile.name}
                className="max-w-full rounded-lg"
              />
            ) : isPdf(selectedFile.name) && fileContent ? (
              <embed
                src={`data:application/pdf;base64,${fileContent}`}
                type="application/pdf"
                className="w-full h-[60vh] rounded-lg"
              />
            ) : isTextPreview(selectedFile.name) && fileContent && extension(selectedFile.name) === 'md' ? (
              <MarkdownRenderer content={fileContent} />
            ) : isTextPreview(selectedFile.name) && fileContent ? (
              <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 p-4 rounded-lg max-h-[60vh] overflow-auto">
                {fileContent}
              </pre>
            ) : (
              <p className="text-slate-400 text-sm">Preview not available for this file type.</p>
            )}
          </div>
          <div className="px-4 pb-4 flex items-center gap-4 text-xs text-slate-400">
            <span>{formatSize(selectedFile.size)}</span>
            <span>SHA: {selectedFile.sha.slice(0, 7)}</span>
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100 overflow-hidden">
        {files.map((item) => (
          <div
            key={item.path}
            className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors group"
          >
            <button
              onClick={() => item.type === 'dir' ? navigateDir(item.path) : selectFile(item)}
              className="flex items-center gap-3 flex-1 text-left"
            >
              <div className={`p-2 rounded-lg ${item.type === 'dir' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'}`}>
                {item.type === 'dir' ? <Folder size={18} /> :
                 isImage(item.name) ? <FileImage size={18} /> :
                 <FileText size={18} />}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-slate-800 truncate">{item.name}</div>
                <div className="text-xs text-slate-400">
                  {item.type === 'dir' ? 'Folder' : formatSize(item.size)}
                </div>
              </div>
            </button>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {item.type === 'file' && (
                <button
                  onClick={() => apiRequest(`/api/files/download?repo=${repoName}&path=${encodeURIComponent(item.path)}`, 'GET')
                    .then(r => {
                      const blob = new Blob([r.content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = item.name;
                      a.click();
                      URL.revokeObjectURL(url);
                    })
                    .catch(() => {})}
                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Download size={15} />
                </button>
              )}
              <button
                onClick={() => handleDelete(item)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilesView;
